import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, FileText, Image as ImageIcon, ShieldCheck, Upload, X,
  Download, Eye, ChevronDown, ChevronUp, CreditCard, Check, Receipt,
  Camera, AlertCircle, Sparkles,
} from 'lucide-react';

/**
 * Batch B Preview — mockup of the morphing Pay/View-Docs button and the
 * three layout options for the embedded receipts/compliance view.
 *
 * No backend wiring. Static demo data. Lets the user click between the three
 * options on a single realistic "Framing Labor" phase and pick one.
 */

const DEMO_PHASE = {
  id: 'demo-framing-labor',
  name: 'Framing Labor',
  estimated_cost: 45800,
  actual_cost: 45800,
  supplier_amount_paid: 45800,
  supplier_paid: true,
  is_paid: true,
  status: 'completed',
  contractor: { name: 'Block 7 Contracting', email: 'hello@block7.com' },
};

const DEMO_DOCS = {
  receipts: [
    { id: 'r1', name: 'Framing materials invoice.pdf', amount: 12400, date: '04/15/2026', kind: 'invoice' },
    { id: 'r2', name: 'Labor week 1 invoice.pdf', amount: 18900, date: '04/22/2026', kind: 'invoice' },
    { id: 'r3', name: 'Labor week 2 invoice.pdf', amount: 14500, date: '04/29/2026', kind: 'invoice' },
  ],
  compliance: {
    w9: { uploaded: true, date: '03/12/2026', filename: 'Block7-W9.pdf' },
    coi: { uploaded: true, date: '03/12/2026', expires: '03/12/2027', filename: 'Block7-COI-2026.pdf' },
    lien_waiver: { uploaded: true, date: '04/30/2026', filename: 'Block7-FinalLienWaiver.pdf' },
    completion_photos: { uploaded: true, count: 8, date: '04/30/2026' },
  },
};

// ----- Shared bits -----------------------------------------------

const StatusPill = ({ tone, children }) => {
  const tones = {
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue:  'bg-blue-50 text-blue-700 border-blue-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${tones[tone] || tones.slate}`}>
      {children}
    </span>
  );
};

const PhaseCard = ({ children, footer, highlight }) => (
  <div className={`bg-white rounded-xl border ${highlight ? 'border-blue-300 shadow-md ring-1 ring-blue-100' : 'border-slate-200 shadow-sm'} overflow-hidden`}>
    <div className="p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-sm font-semibold text-slate-900">{DEMO_PHASE.name}</div>
          <div className="text-xs text-slate-500 mt-0.5">{DEMO_PHASE.contractor.name}</div>
        </div>
        <div className="flex items-center gap-1.5">
          <StatusPill tone="green"><Check className="w-3 h-3" /> Paid</StatusPill>
          <StatusPill tone="slate">Completed</StatusPill>
        </div>
      </div>
      <div className="flex items-baseline gap-4 text-xs text-slate-600">
        <span>Est: <strong className="text-slate-900">${DEMO_PHASE.estimated_cost.toLocaleString()}</strong></span>
        <span>Actual: <strong className="text-slate-900">${DEMO_PHASE.actual_cost.toLocaleString()}</strong></span>
        <span>Paid: <strong className="text-emerald-600">${DEMO_PHASE.supplier_amount_paid.toLocaleString()}</strong></span>
      </div>
    </div>
    {children}
    {footer}
  </div>
);

// Reusable morphing button (the visual primitive)
const MorphingButton = ({ state, onClick, dataTestId }) => {
  // state: 'unpaid' | 'partial' | 'paid'
  const variants = {
    unpaid: {
      label: 'Pay',
      cls: 'bg-emerald-600 text-white hover:bg-emerald-700',
      icon: <CreditCard className="w-3.5 h-3.5" />,
    },
    partial: {
      label: 'Pay More · $19,200 left',
      cls: 'bg-amber-500 text-white hover:bg-amber-600',
      icon: <CreditCard className="w-3.5 h-3.5" />,
    },
    paid: {
      label: 'View Docs',
      cls: 'bg-blue-600 text-white hover:bg-blue-700',
      icon: <FileText className="w-3.5 h-3.5" />,
    },
  };
  const v = variants[state] || variants.paid;
  return (
    <button
      data-testid={dataTestId}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition-colors ${v.cls}`}
    >
      {v.icon} {v.label}
    </button>
  );
};

// ----- The actual docs content (shared by all 3 options) -----------

const DocsContent = ({ compact }) => (
  <div className={compact ? 'space-y-3' : 'space-y-5'}>
    {/* Receipts */}
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Receipts & invoices</div>
        <button className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1">
          <Upload className="w-3 h-3" /> Add receipt
        </button>
      </div>
      <div className="space-y-1.5">
        {DEMO_DOCS.receipts.map(r => (
          <div key={r.id} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2 text-xs">
            <Receipt className="w-4 h-4 text-slate-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-slate-900 truncate">{r.name}</div>
              <div className="text-slate-500">${r.amount.toLocaleString()} · {r.date}</div>
            </div>
            <button className="text-slate-400 hover:text-blue-600 p-1"><Eye className="w-3.5 h-3.5" /></button>
            <button className="text-slate-400 hover:text-blue-600 p-1"><Download className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>

    {/* Compliance */}
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Compliance</div>
      <div className="grid grid-cols-2 gap-1.5">
        {[
          { key: 'w9', label: 'W-9', icon: ShieldCheck, doc: DEMO_DOCS.compliance.w9 },
          { key: 'coi', label: 'COI', icon: ShieldCheck, doc: DEMO_DOCS.compliance.coi, sub: 'Expires ' + DEMO_DOCS.compliance.coi.expires },
          { key: 'lw',  label: 'Lien Waiver', icon: FileText, doc: DEMO_DOCS.compliance.lien_waiver },
          { key: 'photos', label: `${DEMO_DOCS.compliance.completion_photos.count} photos`, icon: Camera, doc: DEMO_DOCS.compliance.completion_photos },
        ].map(({ key, label, icon: Icon, doc, sub }) => (
          <div key={key} className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-xs">
            <div className="flex items-center gap-1.5">
              <Icon className="w-3.5 h-3.5 text-emerald-700" />
              <span className="font-medium text-emerald-900">{label}</span>
              <Check className="w-3.5 h-3.5 text-emerald-600 ml-auto" />
            </div>
            <div className="text-emerald-700 mt-0.5">Uploaded {doc.date}{sub ? ` · ${sub}` : ''}</div>
          </div>
        ))}
      </div>
    </div>

    {/* Refund / corrections (Batch B item #3 surfaces here) */}
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
      <div className="flex items-center gap-2 text-xs">
        <AlertCircle className="w-3.5 h-3.5 text-amber-700" />
        <span className="font-medium text-amber-900">No refunds logged.</span>
        <button className="text-amber-700 hover:text-amber-900 underline ml-auto">Log refund</button>
      </div>
      <div className="text-[11px] text-amber-700 mt-1">Refunds reduce Actual Cost for net spend reporting.</div>
    </div>
  </div>
);

// ----- OPTION A — Inline expanding drawer below the phase row ------

const OptionA = () => {
  const [open, setOpen] = useState(true);
  return (
    <PhaseCard
      highlight={open}
      footer={
        <>
          <div className="flex items-center justify-end gap-2 px-4 pb-3">
            <button className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" /> Start / Edit
            </button>
            <MorphingButton state="paid" onClick={() => setOpen(o => !o)} dataTestId="optA-view-docs" />
          </div>
          {open && (
            <div className="border-t border-slate-100 bg-slate-50/60 p-4 animate-in slide-in-from-top-2 duration-200">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-slate-800 inline-flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" /> Documents for {DEMO_PHASE.name}
                </div>
                <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700">
                  <ChevronUp className="w-4 h-4" />
                </button>
              </div>
              <DocsContent compact />
            </div>
          )}
        </>
      }
    />
  );
};

// ----- OPTION B — Slide-in side panel from the right ---------------

const OptionB = ({ panelOpen, onTogglePanel }) => (
  <PhaseCard
    footer={
      <div className="flex items-center justify-end gap-2 px-4 pb-3 pt-2 border-t border-slate-50">
        <button className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
          <Sparkles className="w-3.5 h-3.5" /> Start / Edit
        </button>
        <MorphingButton state="paid" onClick={onTogglePanel} dataTestId="optB-view-docs" />
      </div>
    }
  />
);

const OptionBPanel = ({ open, onClose }) => (
  <div className={`fixed inset-y-0 right-0 w-[420px] bg-white shadow-2xl border-l border-slate-200 transform transition-transform z-50 ${open ? 'translate-x-0' : 'translate-x-full'}`}>
    <div className="p-4 border-b border-slate-200 flex items-center justify-between">
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Documents</div>
        <div className="text-base font-semibold text-slate-900 mt-0.5">{DEMO_PHASE.name}</div>
        <div className="text-xs text-slate-500 mt-0.5">{DEMO_PHASE.contractor.name}</div>
      </div>
      <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
    </div>
    <div className="p-4 overflow-y-auto max-h-[calc(100vh-100px)]">
      <DocsContent />
    </div>
  </div>
);

// ----- OPTION C — Popover anchored to the button -------------------

const OptionC = () => {
  const [open, setOpen] = useState(false);
  return (
    <PhaseCard
      footer={
        <div className="flex items-center justify-end gap-2 px-4 pb-3 pt-2 border-t border-slate-50 relative">
          <button className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5" /> Start / Edit
          </button>
          <MorphingButton state="paid" onClick={() => setOpen(o => !o)} dataTestId="optC-view-docs" />
          {open && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
              <div className="absolute right-4 bottom-12 w-[380px] bg-white rounded-xl border border-slate-200 shadow-2xl z-40 animate-in fade-in zoom-in-95 duration-150">
                <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-900 inline-flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" /> Documents
                  </div>
                  <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-3 max-h-[480px] overflow-y-auto">
                  <DocsContent compact />
                </div>
              </div>
            </>
          )}
        </div>
      }
    />
  );
};

// ----- Current / broken state (for comparison) ---------------------

const CurrentBroken = () => (
  <PhaseCard
    footer={
      <div className="px-4 pb-3 pt-2 border-t border-slate-50">
        <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 text-xs text-red-800 mb-2 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Today's bug:</strong> clicking "View Docs" opens the 4-step Log Payment drawer, even though the phase is fully paid &amp; complete.
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button className="text-xs text-emerald-600 border border-emerald-300 px-3 py-1.5 rounded-lg hover:bg-emerald-50 inline-flex items-center gap-1">
            <CreditCard className="w-3.5 h-3.5" /> Pay More
          </button>
          <button className="text-xs text-blue-600 border border-blue-300 px-3 py-1.5 rounded-lg hover:bg-blue-50 inline-flex items-center gap-1">
            <Eye className="w-3.5 h-3.5" /> View Docs
          </button>
        </div>
      </div>
    }
  />
);

// ----- Page --------------------------------------------------------

export default function BatchBPreview() {
  const [panelOpen, setPanelOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const Block = ({ tag, label, badge, children }) => (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{tag}</div>
          <h3 className="text-lg font-semibold text-slate-900 mt-0.5">{label}</h3>
        </div>
        {badge && <span className="text-[11px] uppercase font-semibold tracking-wide bg-slate-900 text-white px-2 py-1 rounded">{badge}</span>}
      </div>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 mb-3">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <h1 className="text-3xl font-bold text-slate-900">Batch B preview · Morphing Pay / View Docs button</h1>
          <p className="text-slate-600 mt-2 max-w-3xl">
            Click any "View Docs" button below to compare the three layout options on the same realistic Framing Labor phase
            (fully paid, $45,800, Block 7 Contracting). Currently the button opens the 4-step Log Payment drawer by mistake — the three options below all fix that.
          </p>
        </div>

        {/* Today's bug */}
        <div className="mb-10">
          <Block tag="Today" label="What you're seeing right now" badge="Bug">
            <div className="max-w-md">
              <CurrentBroken />
            </div>
          </Block>
        </div>

        {/* Three options side-by-side on lg+, stacked on smaller */}
        <div className="space-y-10">
          <Block tag="Option A" label="Inline expanding drawer (recommended)" badge="1a">
            <p className="text-sm text-slate-600 mb-4 max-w-2xl">
              Click "View Docs" → the row expands DOWN into a soft-blue panel showing receipts, compliance docs, and a refund slot.
              Feels like a Gmail thread. No context loss, no new page, no modal stack.
            </p>
            <div className="max-w-2xl">
              <OptionA />
            </div>
          </Block>

          <Block tag="Option B" label="Slide-in side panel" badge="1b">
            <p className="text-sm text-slate-600 mb-4 max-w-2xl">
              Click "View Docs" → a 420px panel slides in from the right (like Slack thread / Linear ticket detail). Holds more content; ideal for
              uploading multiple docs without scrolling the whole page. Persistent — closes only when you click X.
            </p>
            <div className="max-w-md">
              <OptionB panelOpen={panelOpen} onTogglePanel={() => setPanelOpen(true)} />
            </div>
            <OptionBPanel open={panelOpen} onClose={() => setPanelOpen(false)} />
          </Block>

          <Block tag="Option C" label="Anchored popover bubble" badge="1c">
            <p className="text-sm text-slate-600 mb-4 max-w-2xl">
              Click "View Docs" → a 380px popover floats above the button (like a hover-card). Compact, fast to dismiss (click anywhere). Trade-off: limited
              real estate, harder to use when uploading multiple docs.
            </p>
            <div className="max-w-md">
              <OptionC />
            </div>
          </Block>
        </div>

        {/* Voting bar */}
        <div className="mt-12 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900 mb-1">Pick one</h3>
          <p className="text-sm text-slate-600 mb-4">Tell me which one feels right and I'll wire it up behind the <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">batch_b_morphing_pay_button</code> feature flag — visible only to your account first.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { key: 'A', label: 'Inline drawer', desc: 'Gmail-thread style' },
              { key: 'B', label: 'Side panel',    desc: 'Slack / Linear style' },
              { key: 'C', label: 'Popover',       desc: 'Hover-card style' },
            ].map(o => (
              <button
                key={o.key}
                onClick={() => setSelected(o.key)}
                className={`text-left rounded-xl border p-4 transition ${selected === o.key ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-200' : 'border-slate-200 hover:border-slate-300'}`}
              >
                <div className="text-xs uppercase tracking-wide font-semibold text-slate-500">Option {o.key}</div>
                <div className="text-base font-semibold text-slate-900 mt-1">{o.label}</div>
                <div className="text-xs text-slate-600 mt-0.5">{o.desc}</div>
              </button>
            ))}
          </div>
          {selected && (
            <div className="mt-4 text-sm text-slate-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <strong>Got it — Option {selected}.</strong> Reply with this choice in chat and I'll start the implementation behind the feature flag.
            </div>
          )}
        </div>

        {/* Also-included */}
        <div className="mt-10 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900 mb-3">Also shipping in Batch B</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-xs uppercase tracking-wide font-semibold text-emerald-700 mb-1">Item 1 of 4</div>
              <div className="font-semibold text-slate-900">Morphing button</div>
              <div className="text-sm text-slate-600 mt-1">One button that visually changes state instead of two side-by-side. Less clutter on every phase row.</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide font-semibold text-emerald-700 mb-1">Item 3 of 4</div>
              <div className="font-semibold text-slate-900">Refunds → net actual cost</div>
              <div className="text-sm text-slate-600 mt-1">Refunds atomically decrement <code className="bg-slate-100 px-1 py-0.5 rounded text-xs">actual_cost</code>. Dashboard "Total Spent" reflects net automatically. Historical data untouched.</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide font-semibold text-emerald-700 mb-1">Item 4 of 4</div>
              <div className="font-semibold text-slate-900">Inline ▶ Start / ✓ Complete / ✎ Edit</div>
              <div className="text-sm text-slate-600 mt-1">Quick actions on every phase row, not buried in the 3-dot menu.</div>
            </div>
          </div>
        </div>

        <div className="mt-10 text-center text-xs text-slate-400">
          Preview only · no DB changes · feature flag <code>batch_b_morphing_pay_button</code> is OFF for all users until your sign-off.
        </div>
      </div>
    </div>
  );
}
